import React, { useState, useEffect, useMemo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import StandardDialog from '../../Modals/StandardDialog';
import { StandardDialogActions } from '../../Modals/StandardDialog';
import { FormControl, InputLabel, MenuItem, Select, Box, Typography, Button, TextField } from '@mui/material';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import { REMEDIATION_OPTIONS, REMEDIATION_VERIFICATION_STATUS } from '../../../services/surveyHelpers';
import { useAppSelector } from '../../../app/hooks';
import { useForm } from 'react-hook-form';
import { USER_TYPES } from '../../../services/user';
import { LABELS } from '../../../utils/labels';
import { SubRemediation } from '../../../interfaces/assessment.interface';

const commonBoxStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px',
  paddingLeft: '16px',
  borderRadius: '4px',
  height: '50px',
  mt: 1,
  mb: 3,
};

const commonTypographyStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '16px',
  fontWeight: 400,
  flexGrow: 1,
  color: theme.palette.customThemeColors?.darkGrey,
});

const commonButtonStyles = {
  ml: 2,
};

export default function RemediationValidation({
  subRemediation,
  origRemediationStatus,
  origVerificationStatus,
  origVerificationDate,
  origVerificationComment,
  isSubRemediation,
  patchRemediationValidationStatus,
  isV53,
}: {
  subRemediation?: SubRemediation,
  origRemediationStatus?: string,
  origVerificationStatus?: string,
  origVerificationDate?: string,
  isSubRemediation?: boolean,
  origVerificationComment?: string,
  patchRemediationValidationStatus: (status: string, isSubRemediation: boolean, comment: string) => void
  isV53: boolean,
}) {
  const {
    handleSubmit,
  } = useForm();
  const theme = useTheme();
  const { user } = useAppSelector((state) => state.user);
  const [showVerifyMod, setShowVerifyMod] = useState(false);
  const [validationStatus, setValidationStatus] = useState(origVerificationStatus);
  const [comment, setComment] = useState(origVerificationComment || '');
  const showIncompleteError = useMemo(() => {
    const { remediationDate, remediationPlan, remediationComment } = subRemediation || {};
    return !(!!remediationDate && !!remediationComment && remediationPlan === REMEDIATION_OPTIONS.remediated);
  }, [subRemediation]);

  const formattedDate = origVerificationDate ? moment(origVerificationDate).format('MMMM D, YYYY') : 'Date not available';
  const handleSaveClick = () => {
    patchRemediationValidationStatus(validationStatus || '', isSubRemediation || false, comment || '');
    setShowVerifyMod(false);
  };

  useEffect(() => {
    setComment(origVerificationComment || '');
    setValidationStatus(origVerificationStatus || '');
  }, [origVerificationComment, origVerificationStatus]);

  return (
    <>
      { ((user?.type === USER_TYPES.TPN_ADMIN || !isV53) && origVerificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed) && (
        <Box
          sx={{
            ...commonBoxStyles,
            backgroundColor: 'rgba(76, 175, 80, 0.07)',
          }}
        >
          <Typography sx={commonTypographyStyles(theme)}>
            <CheckCircleIcon sx={{ fontSize: '16px', mr: 1 }} color="success" />
            TPN Admin Validated - {formattedDate}
          </Typography>
            <Button variant="contained" color="primary" onClick={() => setShowVerifyMod(true)} sx={commonButtonStyles}>
            {user?.type === USER_TYPES.TPN_ADMIN ? 'Validate' : 'View Details' }
            </Button>
        </Box>
      )}
      {origVerificationStatus === REMEDIATION_VERIFICATION_STATUS.rejected && (
        <Box
          sx={{
            ...commonBoxStyles,
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
          }}
        >
          <Typography sx={commonTypographyStyles(theme)}>
            <ErrorIcon sx={{ fontSize: '16px', mr: 1 }} color="error" />
            Rejected By TPN Admin - {formattedDate}
          </Typography>
            <Button variant="contained" color="primary" onClick={() => setShowVerifyMod(true)} sx={commonButtonStyles}>
              {user?.type === USER_TYPES.TPN_ADMIN ? 'Validate' : 'View Details' }
            </Button>
        </Box>
      )}
      { (user?.type === USER_TYPES.TPN_ADMIN || !isV53) && origVerificationStatus === REMEDIATION_VERIFICATION_STATUS.notStarted && origRemediationStatus === REMEDIATION_OPTIONS.remediated && (
        <Box
          sx={{
            ...commonBoxStyles,
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
          }}
        >
          <Typography sx={commonTypographyStyles(theme)}>
            <AccessTimeIcon sx={{ fontSize: '16px', mr: 1 }} color="warning" />
            Pending TPN Admin Validation
          </Typography>
            <Button variant="contained" color="primary" onClick={() => setShowVerifyMod(true)} sx={commonButtonStyles}>
            {user?.type === USER_TYPES.TPN_ADMIN ? 'Validate' : 'View Details' }
            </Button>
        </Box>
      )}
      <StandardDialog
        maxWidth="sm"
        title={LABELS.remediationValidation}
        handleClose={() => setShowVerifyMod(false)}
        isOpen={showVerifyMod}
      >
      <Box component="form" method="post" onSubmit={handleSubmit(handleSaveClick)}>
        <Box sx={{ my: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="validation-status-label">Validation Status</InputLabel>
            <Select
              labelId="validation-status-label"
              id="remediation-validation-status-select"
              value={validationStatus}
              disabled={user?.type !== USER_TYPES.TPN_ADMIN || showIncompleteError}
              onChange={(e) => setValidationStatus(e.target.value)}
              label="Validation Status"
            >
              <MenuItem value={REMEDIATION_VERIFICATION_STATUS.notStarted}>Not Started</MenuItem>
              <MenuItem value={REMEDIATION_VERIFICATION_STATUS.reviewed}>Reviewed</MenuItem>
              <MenuItem value={REMEDIATION_VERIFICATION_STATUS.rejected}>Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField
          label="Comment"
          id="validation-comment"
          multiline
          rows={4}
          value={comment}
          required
          disabled={user?.type !== USER_TYPES.TPN_ADMIN || showIncompleteError}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          sx={{ marginTop: 2 }}
        />
        {user?.type === USER_TYPES.TPN_ADMIN && (
          <StandardDialogActions>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              {showIncompleteError && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mb: 1, fontSize: 13 }}
                >
                  Remediation Plan, Date, and Comment are required for Validation.
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button variant="outlined" onClick={() => setShowVerifyMod(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={showIncompleteError}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </StandardDialogActions>
        )}
        </Box>
      </StandardDialog>
    </>
  );
}